import {
  type APIMessage,
  ChannelType,
  formatShortTime,
  type PendingMessage,
  selectIsUnread,
  snowflakeTimestamp,
} from '@alternativamais/chat-core';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import {
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { avisarErro } from '../../componentes/Avisos';
import { Avatar } from '../../componentes/Avatar';
import { Icone } from '../../componentes/Icone';
import { abrirCamada } from '../../chat/camadas';
import { IconeDoGrupo } from './cabecalho/Cabecalho';
import { useCliente, useEstado } from '../../chat/contexto';
import { useNome } from '../../chat/ganchos';
import { caber } from './mensagem/Conteudo';
import {
  DivisoriaDia,
  DivisoriaNovas,
  Esqueleto,
  Mensagem,
  MensagemPendente,
} from './mensagem/Mensagem';
import { type Linha, montarLinhas } from '@alternativamais/chat-core';

/** O canal pede à lista: pular para uma mensagem, ir para o fim. */
export type ControleLista = {
  irPara(id: string): void;
  irParaOFim(): void;
};

const SEM_PENDENTES: PendingMessage[] = [];
/** Abaixo disto (px) do fim, a lista está "no fim" (segue as novas e marca como lida). */
const NO_FIM = 48;
/** A esta distância (px) da ponta, já carrega a próxima página. */
const PERTO = 600;
/** Espaço do esqueleto no alto enquanto há mensagens mais antigas. */
const ALTURA_CARREGANDO = 230;

/** Posição de cada canal ao sair (volta onde estava, como o Discord). */
const posicoes = new Map<string, { offset: number; medidas: VirtualItem[] }>();

function estimar(linha: Linha): number {
  switch (linha.tipo) {
    case 'inicio':
      return 190;
    case 'carregando':
      return ALTURA_CARREGANDO;
    case 'dia':
      return 41;
    case 'novas':
      return 33;
    case 'pendente':
      return linha.agrupada ? 26 : 62;
    case 'mensagem': {
      const mensagem = linha.mensagem;
      let altura = linha.agrupada ? 26 : 64;
      altura += Math.floor(mensagem.content.length / 110) * 22;
      altura += (mensagem.content.match(/\n/g)?.length ?? 0) * 22;
      for (const anexo of mensagem.attachments) {
        altura +=
          anexo.width && anexo.height
            ? caber(anexo.width, anexo.height).height + 6
            : 70;
      }
      altura += mensagem.embeds.length * 140;
      if (mensagem.reactions?.length) altura += 34;
      if (mensagem.message_reference && mensagem.type === 19) altura += 22;
      return altura;
    }
  }
}

/**
 * Mensagens do canal, virtualizadas (só as linhas visíveis existem no DOM).
 * Presa ao fim: novas mensagens seguem a rolagem; mais antigas entram por
 * cima sem a tela pular. Chegando ao fim, marca o canal como lido.
 */
export function ListaDeMensagens({
  canalId,
  guildId,
  controle,
  aoDestacar,
  mensagemInicial,
}: {
  canalId: string;
  guildId: string | null;
  controle: RefObject<ControleLista | null>;
  aoDestacar: (id: string) => void;
  /** Link para uma mensagem: abre o canal nela. */
  mensagemInicial?: string;
}) {
  const cliente = useCliente();
  const lista = useEstado((estado) => estado.messages[canalId]);
  const pendentes =
    useEstado((estado) => estado.pending[canalId]) ?? SEM_PENDENTES;
  const euId = useEstado((estado) => estado.me?.id ?? null);
  const naoLida = useEstado(
    (estado) =>
      selectIsUnread(estado, canalId) ||
      (estado.readStates[canalId]?.mention_count ?? 0) > 0,
  );
  const [ultimoLido, setUltimoLido] = useState(
    () => cliente.store.getState().readStates[canalId]?.last_message_id ?? null,
  );
  const [focada, setFocada] = useState(
    () => document.visibilityState === 'visible' && document.hasFocus(),
  );

  const mensagens = useMemo<APIMessage[]>(
    () => (lista ? lista.ids.map((id) => lista.byId[id]) : []),
    [lista],
  );
  const linhas = useMemo(
    () =>
      lista?.loaded
        ? montarLinhas({
            mensagens,
            pendentes,
            temMaisAntes: lista.hasMoreBefore,
            temMaisDepois: lista.hasMoreAfter,
            ultimoLido,
            euId,
          })
        : [],
    [lista, mensagens, pendentes, ultimoLido, euId],
  );

  const rolagem = useRef<HTMLDivElement>(null);
  const [salvo] = useState(() => posicoes.get(canalId));
  const virtual = useVirtualizer({
    count: linhas.length,
    getScrollElement: () => rolagem.current,
    estimateSize: (indice) => estimar(linhas[indice]),
    getItemKey: (indice) => linhas[indice].chave,
    overscan: 10,
    anchorTo: 'end',
    followOnAppend: true,
    scrollEndThreshold: NO_FIM,
    paddingStart: lista?.hasMoreBefore ? ALTURA_CARREGANDO : 0,
    paddingEnd: 12,
    initialOffset: salvo?.offset,
    initialMeasurementsCache: salvo?.medidas,
  });

  // Para onde rolar quando as linhas chegarem: para a mensagem de um link
  // (carregada em volta dela) ou para o fim (voltando ao presente). Atendido
  // antes de pintar, sem esperar o próximo quadro.
  const pedidoDeRolagem = useRef<
    { para: 'mensagem'; id: string } | { para: 'fim' } | null
  >(null);
  const posicionado = useRef(false);

  // Carrega o fim do canal na primeira vez (e de novo depois de um READY);
  // com link para uma mensagem, carrega em volta dela. Pedidos iguais em
  // andamento o core junta num só.
  useEffect(() => {
    if (lista?.loaded) return;
    const inicial = posicionado.current ? undefined : mensagemInicial;
    if (inicial) pedidoDeRolagem.current = { para: 'mensagem', id: inicial };
    cliente
      .loadMessages(canalId, inicial ? { around: inicial } : {})
      .catch(avisarErro);
  }, [cliente, canalId, lista?.loaded, mensagemInicial]);

  // Primeira vez com mensagens: vai para o fim (ou para onde estava); e
  // atende o pedido de rolagem quando as linhas dele chegam.
  useLayoutEffect(() => {
    if (!linhas.length) return;
    const pedido = pedidoDeRolagem.current;
    if (!posicionado.current) {
      posicionado.current = true;
      if (!pedido) {
        if (!salvo) virtual.scrollToEnd();
        return;
      }
    }
    if (!pedido) return;
    if (pedido.para === 'fim') {
      pedidoDeRolagem.current = null;
      virtual.scrollToEnd();
      return;
    }
    const indice = linhas.findIndex((linha) => linha.chave === pedido.id);
    if (indice < 0) return;
    pedidoDeRolagem.current = null;
    virtual.scrollToIndex(indice, { align: 'center' });
    aoDestacar(pedido.id);
  }, [linhas, salvo, virtual, aoDestacar]);

  // Guarda a posição ao sair do canal (se não estava no fim).
  useEffect(
    () => () => {
      if (!rolagem.current) return;
      if (virtual.isAtEnd(NO_FIM)) posicoes.delete(canalId);
      else {
        posicoes.set(canalId, {
          offset: rolagem.current.scrollTop,
          medidas: virtual.measurementsCache,
        });
      }
    },
    [canalId, virtual],
  );

  // Perto da ponta (pela rolagem de verdade, não pela estimativa do primeiro
  // desenho): carrega mais antigas ou mais novas.
  const itens = virtual.getVirtualItems();
  const primeiro = itens[0]?.index ?? -1;
  const ultimo = itens[itens.length - 1]?.index ?? -1;
  const carregando = useRef(false);
  useEffect(() => {
    const elemento = rolagem.current;
    if (
      !lista?.loaded ||
      carregando.current ||
      !posicionado.current ||
      pedidoDeRolagem.current ||
      !elemento
    ) {
      return;
    }
    const doTopo = elemento.scrollTop;
    const doFim =
      elemento.scrollHeight - elemento.scrollTop - elemento.clientHeight;
    let pedido: Promise<unknown> | null = null;
    if (lista.hasMoreBefore && doTopo < PERTO) {
      pedido = cliente.loadOlder(canalId);
    } else if (lista.hasMoreAfter && doFim < PERTO) {
      pedido = cliente.loadNewer(canalId);
    }
    if (!pedido) return;
    carregando.current = true;
    pedido.catch(avisarErro).finally(() => {
      carregando.current = false;
    });
  }, [cliente, canalId, lista, primeiro, ultimo]);

  // Marca como lido quando a pessoa está no fim, com a janela em foco.
  useEffect(() => {
    const atualizar = () =>
      setFocada(document.visibilityState === 'visible' && document.hasFocus());
    window.addEventListener('focus', atualizar);
    window.addEventListener('blur', atualizar);
    document.addEventListener('visibilitychange', atualizar);
    return () => {
      window.removeEventListener('focus', atualizar);
      window.removeEventListener('blur', atualizar);
      document.removeEventListener('visibilitychange', atualizar);
    };
  }, []);
  const noFim = !!rolagem.current && virtual.isAtEnd(NO_FIM);
  useEffect(() => {
    if (!naoLida || !focada || !noFim || !lista?.loaded || lista.hasMoreAfter)
      return;
    const espera = window.setTimeout(
      () => cliente.ack(canalId).catch(() => undefined),
      300,
    );
    return () => window.clearTimeout(espera);
  }, [
    cliente,
    canalId,
    naoLida,
    focada,
    noFim,
    lista?.loaded,
    lista?.hasMoreAfter,
  ]);

  // Ir para o presente: vendo mensagens antigas, carrega o fim do canal
  // (a rolagem acontece quando as linhas chegam); senão, só rola.
  const irParaOFim = useCallback(() => {
    if (lista?.hasMoreAfter) {
      pedidoDeRolagem.current = { para: 'fim' };
      cliente.loadMessages(canalId).catch((erro) => {
        pedidoDeRolagem.current = null;
        avisarErro(erro);
      });
    } else {
      virtual.scrollToEnd();
    }
  }, [cliente, canalId, lista?.hasMoreAfter, virtual]);

  // Mandei mensagem: some o "Novas" e vai para o fim (o presente).
  const quantasPendentes = useRef(pendentes.length);
  useEffect(() => {
    const antes = quantasPendentes.current;
    quantasPendentes.current = pendentes.length;
    if (pendentes.length <= antes) return;
    setUltimoLido(null);
    irParaOFim();
  }, [pendentes.length, irParaOFim]);

  // Pular para uma mensagem: na lista, rola; fora, carrega em volta dela.
  const irPara = useCallback(
    (id: string) => {
      const indice = linhas.findIndex((linha) => linha.chave === id);
      if (indice >= 0) {
        virtual.scrollToIndex(indice, { align: 'center' });
        aoDestacar(id);
        return;
      }
      pedidoDeRolagem.current = { para: 'mensagem', id };
      cliente.loadMessages(canalId, { around: id }).catch((erro) => {
        pedidoDeRolagem.current = null;
        avisarErro(erro);
      });
    },
    [linhas, virtual, aoDestacar, cliente, canalId],
  );

  useImperativeHandle(controle, () => ({ irPara, irParaOFim }), [
    irPara,
    irParaOFim,
  ]);

  // Outro link para mensagem no mesmo canal (a lista já carregada).
  const ultimoLink = useRef(mensagemInicial);
  useEffect(() => {
    if (!mensagemInicial || mensagemInicial === ultimoLink.current) return;
    ultimoLink.current = mensagemInicial;
    irPara(mensagemInicial);
  }, [mensagemInicial, irPara]);

  const indiceNovas = linhas.findIndex((linha) => linha.tipo === 'novas');
  const novasAcima = indiceNovas >= 0 && primeiro > indiceNovas;
  const quantasNovas =
    indiceNovas >= 0
      ? linhas.slice(indiceNovas).filter((linha) => linha.tipo === 'mensagem')
          .length
      : 0;
  const longeDoFim =
    !!rolagem.current &&
    (lista?.hasMoreAfter ||
      virtual.getTotalSize() -
        (rolagem.current.scrollTop + rolagem.current.clientHeight) >
        rolagem.current.clientHeight * 2);

  return (
    <div className="relative flex min-h-0 grow flex-col">
      {novasAcima && ultimoLido && (
        <div
          role="status"
          className="absolute inset-x-4 top-0 z-[3] flex h-7 items-center justify-between gap-3 rounded-b-lg bg-destaque px-3 text-[13px] font-semibold text-destaque-texto"
        >
          <button
            type="button"
            className="truncate"
            onClick={() =>
              virtual.scrollToIndex(indiceNovas, { align: 'start' })
            }
          >
            {quantasNovas}
            {lista?.hasMoreBefore && quantasNovas >= mensagens.length
              ? '+'
              : ''}{' '}
            {quantasNovas === 1 ? 'mensagem nova' : 'mensagens novas'} desde{' '}
            {formatShortTime(snowflakeTimestamp(ultimoLido))}
          </button>
          <button
            type="button"
            className="flex shrink-0 items-center gap-1.5"
            onClick={() => {
              setUltimoLido(null);
              cliente.ack(canalId).catch(() => undefined);
            }}
          >
            Marcar como lida
            <Icone nome="check" tamanho={16} />
          </button>
        </div>
      )}
      <div
        ref={rolagem}
        role="log"
        aria-label="Mensagens"
        className="rolagem min-h-0 grow overflow-x-hidden overflow-y-auto [overflow-anchor:none]"
      >
        <div className="flex min-h-full flex-col justify-end">
          {!lista?.loaded ? (
            <div
              role="status"
              aria-busy="true"
              aria-label="Carregando mensagens"
              className="pb-3"
            >
              <Esqueleto linhas={5} />
            </div>
          ) : (
            <div
              className="relative w-full"
              style={{ height: virtual.getTotalSize() }}
            >
              {lista.hasMoreBefore && (
                <div
                  role="status"
                  aria-label="Carregando mensagens antigas"
                  className="absolute inset-x-0 top-0 overflow-hidden py-2"
                  style={{ height: ALTURA_CARREGANDO }}
                >
                  <Esqueleto linhas={4} />
                </div>
              )}
              {itens.map((item) => (
                <div
                  key={item.key}
                  data-index={item.index}
                  ref={virtual.measureElement}
                  className="absolute top-0 left-0 w-full"
                  style={{ transform: `translateY(${item.start}px)` }}
                >
                  <LinhaDaLista
                    linha={linhas[item.index]}
                    canalId={canalId}
                    guildId={guildId}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {longeDoFim && (
        <div className="pointer-events-none absolute inset-x-4 bottom-0 z-[3] flex justify-center">
          <button
            type="button"
            onClick={irParaOFim}
            className="pointer-events-auto mb-2 flex h-8 items-center gap-2 rounded-full border border-borda bg-elevado px-3.5 text-[13px] font-semibold shadow-[0_4px_16px_rgba(0,0,0,.35)] hover:bg-ativo"
          >
            {lista?.hasMoreAfter
              ? 'Você está vendo mensagens antigas · Ir para o presente'
              : 'Ir para o presente'}
            <Icone nome="seta-baixo" tamanho={16} />
          </button>
        </div>
      )}
    </div>
  );
}

function LinhaDaLista({
  linha,
  canalId,
  guildId,
}: {
  linha: Linha;
  canalId: string;
  guildId: string | null;
}) {
  switch (linha.tipo) {
    case 'inicio':
      return <InicioDoCanal canalId={canalId} guildId={guildId} />;
    case 'carregando':
      return (
        <div role="status" aria-label="Carregando mensagens" className="py-2">
          <Esqueleto linhas={4} />
        </div>
      );
    case 'dia':
      return <DivisoriaDia data={linha.data} />;
    case 'novas':
      return <DivisoriaNovas />;
    case 'mensagem':
      return <Mensagem mensagem={linha.mensagem} agrupada={linha.agrupada} />;
    case 'pendente':
      return (
        <MensagemPendente pendente={linha.pendente} agrupada={linha.agrupada} />
      );
  }
}

/** Começo do canal (C28) ou da conversa. */
function InicioDoCanal({
  canalId,
  guildId,
}: {
  canalId: string;
  guildId: string | null;
}) {
  const canal = useEstado((estado) => estado.channels[canalId]);
  const dono = useNome(canal?.owner_id ?? '', guildId);
  const outra =
    canal?.type === ChannelType.DM ? canal.recipients?.[0] : undefined;
  if (outra) return <InicioDaConversa pessoa={outra} />;
  if (canal?.type === ChannelType.GROUP_DM) {
    const nome =
      canal.name ||
      (canal.recipients ?? [])
        .map((item) => item.global_name ?? item.username)
        .join(', ');
    return (
      <section className="flex flex-col items-start gap-2 px-4 pt-4 pb-6">
        <IconeDoGrupo canal={canal} tamanho={80} />
        <h2 className="mt-2 mb-0 font-titulo text-[32px] leading-tight font-bold">
          {nome}
        </h2>
        <p className="m-0 text-[15px] text-texto-2">
          Este é o começo do grupo{' '}
          <strong className="font-semibold text-texto">{nome}</strong>
          {canal.owner_id ? `, criado por ${dono}` : ''}.
        </p>
        <button
          type="button"
          onClick={() => abrirCamada({ tipo: 'config-grupo', canalId })}
          className="mt-1 h-8 rounded-lg bg-ativo px-3 text-sm font-semibold hover:brightness-110"
        >
          Editar grupo
        </button>
      </section>
    );
  }
  const thread =
    canal?.type === ChannelType.PUBLIC_THREAD ||
    canal?.type === ChannelType.PRIVATE_THREAD;
  return (
    <section className="flex flex-col items-start gap-2 px-4 pt-4 pb-6">
      <span className="flex size-[68px] items-center justify-center rounded-full bg-ativo">
        <Icone nome={thread ? 'thread' : 'hash'} tamanho={36} />
      </span>
      <h2 className="mt-2 mb-0 font-titulo text-[32px] leading-tight font-bold">
        {thread ? canal?.name : `Boas-vindas ao #${canal?.name ?? 'canal'}!`}
      </h2>
      <p className="m-0 text-[15px] text-texto-2">
        {thread
          ? `Iniciada por ${dono}.`
          : `Este é o começo do canal #${canal?.name ?? ''}.`}
      </p>
    </section>
  );
}

function InicioDaConversa({
  pessoa,
}: {
  pessoa: NonNullable<APIMessage['author']>;
}) {
  const nome = useNome(pessoa.id, null, pessoa);
  return (
    <section className="flex flex-col items-start gap-2 px-4 pt-4 pb-6">
      <Avatar pessoa={pessoa} nome={nome} tamanho={80} />
      <h2 className="mt-2 mb-0 font-titulo text-[32px] leading-tight font-bold">
        {nome}
      </h2>
      <span className="text-lg text-texto-2">{pessoa.username}</span>
      <p className="m-0 text-[15px] text-texto-2">
        Este é o começo do seu histórico de mensagens diretas com{' '}
        <strong className="font-semibold text-texto">{nome}</strong>.
      </p>
      <button
        type="button"
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          abrirCamada({
            tipo: 'perfil',
            userId: pessoa.id,
            guildId: null,
            ancora: { x: caixa.right + 8, y: caixa.top },
          });
        }}
        className="mt-1 h-8 rounded-lg bg-ativo px-3 text-sm font-semibold hover:brightness-110"
      >
        Ver perfil
      </button>
    </section>
  );
}
